// Midway authentication for the Ticket Insights SPA.
//
// Flow: when the app loads without a valid Midway id_token, it performs a
// full-page redirect to Midway SSO. Midway authenticates the user (security
// key) and redirects back to this app with `?id_token=<jwt>`. We validate the
// nonce, stash the token in sessionStorage, strip it from the URL, and use it
// as the Bearer token for API Gateway calls. The API Gateway Midway authorizer
// validates the token server-side — the client never trusts it for access
// decisions (see ticket V2306137056).
//
// Notes:
// - No fallback/test token is ever used (that was the V2306137056 bypass).
// - The token lives in sessionStorage (not localStorage) and is a short-lived
//   Midway id_token; it is cleared on 401 and on tab close.

const MIDWAY_SSO_URL = 'https://midway-auth.amazon.com/SSO/redirect';

const TOKEN_KEY = 'tickety-midway-token';
const NONCE_KEY = 'tickety-midway-nonce';
const REDIRECT_COUNT_KEY = 'tickety-midway-redirect-count';

// Guard against infinite redirect loops (e.g. Midway returns us without a
// usable token, or a nonce mismatch keeps rejecting the token).
const MAX_REDIRECTS = 3;

// Refresh slightly before the real expiry to avoid racing the clock.
const EXPIRY_SKEW_SECONDS = 60;

/**
 * Local development runs against a mock/local API with no Midway. Detect it by
 * hostname (same approach as rum.ts) so we never redirect to Midway from a dev
 * machine. This is NOT a security bypass: the deployed API Gateway authorizer
 * rejects any request that lacks a valid Midway token regardless of this flag.
 */
function isLocalDev(): boolean {
  return (
    typeof window !== 'undefined' &&
    /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)
  );
}

function generateNonce(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split('.');
  if (parts.length !== 3) throw new Error('Malformed JWT');
  const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  return JSON.parse(atob(padded)) as Record<string, unknown>;
}

function isTokenExpired(token: string): boolean {
  try {
    const exp = decodeJwtPayload(token).exp;
    if (typeof exp !== 'number') return true;
    return Date.now() / 1000 > exp - EXPIRY_SKEW_SECONDS;
  } catch {
    return true;
  }
}

/** Current URL with any id_token param and hash stripped — used as redirect target. */
function getCleanRedirectUri(): string {
  const url = new URL(window.location.href);
  url.searchParams.delete('id_token');
  url.hash = '';
  return url.toString();
}

/**
 * If we've just returned from Midway, capture the id_token from the URL,
 * validate its nonce, persist it, and clean the URL. Returns the token if a
 * valid one was captured, else null.
 */
function captureTokenFromUrl(): string | null {
  const search = new URLSearchParams(window.location.search);
  let token = search.get('id_token');
  if (!token && window.location.hash) {
    token = new URLSearchParams(window.location.hash.substring(1)).get('id_token');
  }
  if (!token) return null;

  // Validate the nonce we stored before redirecting, to reject replayed or
  // injected tokens.
  const expectedNonce = sessionStorage.getItem(NONCE_KEY);
  if (expectedNonce) {
    try {
      if (decodeJwtPayload(token).nonce !== expectedNonce) return null;
    } catch {
      return null;
    }
    sessionStorage.removeItem(NONCE_KEY);
  }

  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.removeItem(REDIRECT_COUNT_KEY);

  // Strip the token out of the address bar / history so it isn't leaked.
  const clean = new URL(window.location.href);
  clean.searchParams.delete('id_token');
  clean.hash = '';
  window.history.replaceState(null, '', clean.pathname + clean.search);

  return token;
}

/**
 * Full-page redirect to Midway SSO to obtain an id_token. Guards against
 * redirect loops. redirect_uri and client_id are derived from this app's own
 * origin (never user input), so there is no open-redirect surface.
 * Returns false only when the redirect loop guard has tripped.
 */
export function redirectToMidway(): boolean {
  const count = parseInt(sessionStorage.getItem(REDIRECT_COUNT_KEY) ?? '0', 10);
  if (count >= MAX_REDIRECTS) {
    sessionStorage.removeItem(REDIRECT_COUNT_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    return false;
  }
  sessionStorage.setItem(REDIRECT_COUNT_KEY, String(count + 1));

  const nonce = generateNonce();
  sessionStorage.setItem(NONCE_KEY, nonce);

  const params = new URLSearchParams({
    response_type: 'id_token',
    client_id: window.location.host,
    redirect_uri: getCleanRedirectUri(),
    scope: 'openid',
    nonce,
  });
  window.location.assign(`${MIDWAY_SSO_URL}?${params.toString()}`);
  return true;
}

/**
 * Returns a valid Midway id_token, or null if none is available without a
 * redirect. Does not itself redirect — callers decide (bootstrap gate redirects
 * up front; the API client redirects on a 401). Returns null in local dev.
 */
export async function getMidwayToken(): Promise<string | null> {
  if (isLocalDev()) return null;

  const fromUrl = captureTokenFromUrl();
  if (fromUrl && !isTokenExpired(fromUrl)) return fromUrl;

  const stored = sessionStorage.getItem(TOKEN_KEY);
  if (stored && !isTokenExpired(stored)) return stored;

  return null;
}

/**
 * Bootstrap gate: ensure the user is authenticated before the app renders.
 * - Local dev: always allowed.
 * - Valid token (freshly captured from URL or stored): allowed.
 * - Otherwise: redirect to Midway and return false (page is navigating away),
 *   unless the redirect loop guard has tripped, in which case return false so
 *   the caller can render an auth-error state instead of looping.
 */
export async function ensureAuthenticated(): Promise<boolean> {
  if (isLocalDev()) return true;
  const token = await getMidwayToken();
  if (token) return true;
  redirectToMidway();
  return false;
}

/** True only when the redirect loop guard has been exhausted (auth is stuck). */
export function isAuthStuck(): boolean {
  return parseInt(sessionStorage.getItem(REDIRECT_COUNT_KEY) ?? '0', 10) >= MAX_REDIRECTS;
}

/** Clear the cached token (e.g. after a 401) so the next call re-authenticates. */
export function clearToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}
